import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Etiqueta } from "../../components/ui/primitivos";
import {
  cambiarEstadoOrden,
  type EstadoImpresora,
  fetchOrden,
  type OrdenListada,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { ESTADOS_IMPRESORA } from "../impresoras/estado";
import { exigeMotivo, puedeForzar } from "./cambio-de-estado";
import { DialogoCambioDeEstado, type ModoDelDialogo } from "./DialogoCambioDeEstado";
import { esSoloLectura } from "./eliminar";
import { etiquetaDeEstado, TONO } from "./estados";

/**
 * Edición inline en el tablero (SPEC 0003 fase 3, `PRD.md` §10.4).
 *
 * `imprenta` la resuelve con popovers sobre la fila. Se portea el
 * comportamiento, con **una diferencia de fondo**:
 *
 * **Las transiciones las decide el worker, no una copia en el cliente.** Allá
 * el componente lee `TRANSICIONES_VALIDAS`, una tabla duplicada en el frontend,
 * y decide quién puede por NOMBRE DE ROL (`hasRole('Manager')`). Acá el popover
 * pide el detalle al abrirse y muestra `transicionesDisponibles`, que el worker
 * ya calculó **para esta persona** aplicando permisos y condiciones de datos
 * (ADR 0024). Salir del flujo es el diálogo de forzar (ADR 0098 B14). Una copia de las reglas se desincroniza, y el síntoma es ofrecer
 * un cambio que después el servidor rechaza.
 *
 * Se pide al ABRIR y no al listar: con 300 filas, traer las transiciones de
 * cada una sería una consulta por fila para algo que casi nadie va a usar.
 */

export function EstadoInline({
  orden,
  alCambiar,
}: {
  orden: OrdenListada;
  alCambiar: () => Promise<void>;
}) {
  const { can, canCodigo } = useSession();
  const [abierto, setAbierto] = useState(false);
  const [disponibles, setDisponibles] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [dialogo, setDialogo] = useState<ModoDelDialogo | null>(null);

  const conSalto = puedeForzar(canCodigo);

  // Sin permiso de operar ni de saltar, la etiqueta no es clickeable. Ocultar
  // no es autorizar (invariante 5) —el endpoint valida igual—, pero ofrecer
  // algo que va a dar 403 es peor que no ofrecerlo.
  // Una ELIMINADA es de solo lectura: toda escritura es 409 (ADR 0100 B8).
  if (
    esSoloLectura(orden.estado) ||
    (!can("produccion", "estados") && !can("produccion", "asignar") && !conSalto)
  ) {
    return (
      <Etiqueta tono={TONO[orden.estado] ?? "neutral"}>{etiquetaDeEstado(orden.estado)}</Etiqueta>
    );
  }

  async function alAbrir(v: boolean) {
    setAbierto(v);
    setError(null);
    if (!v) return;
    setDisponibles(null);
    try {
      const detalle = await fetchOrden(orden.id);
      setDisponibles(detalle.transicionesDisponibles);
    } catch {
      setDisponibles([]);
      setError("No se pudieron leer las transiciones");
    }
  }

  async function aplicar(destino: string) {
    setTrabajando(true);
    setError(null);
    try {
      await cambiarEstadoOrden(orden.id, destino);
      setAbierto(false);
      await alCambiar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cambiar el estado");
    } finally {
      setTrabajando(false);
    }
  }

  /** El popover se cierra y el diálogo toma la pantalla: son dos capas modales. */
  function abrirDialogo(modo: ModoDelDialogo) {
    setAbierto(false);
    setDialogo(modo);
  }

  // Solo las transiciones ordinarias que el worker calculó para esta persona
  // (ADR 0098 B14). Salir del flujo es el diálogo de forzar, con motivo: nunca
  // un clic en la lista.
  const opciones = disponibles ?? [];

  return (
    <>
      <Popover.Root open={abierto} onOpenChange={alAbrir}>
        <Popover.Trigger asChild>
          <button type="button" className="flex items-center gap-1" disabled={trabajando}>
            <Etiqueta tono={TONO[orden.estado] ?? "neutral"}>
              {etiquetaDeEstado(orden.estado)}
            </Etiqueta>
            <ChevronDown className="h-3 w-3 text-neutral-400" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            sideOffset={4}
            className="z-50 w-72 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
          >
            {error ? <p className="px-2 py-1 text-red-700 text-xs">{error}</p> : null}

            {disponibles === null ? (
              <p className="px-2 py-3 text-center text-neutral-500 text-xs">Cargando…</p>
            ) : opciones.length === 0 ? (
              <p className="px-2 py-3 text-center text-neutral-500 text-xs">
                No hay transiciones disponibles para vos en este estado.
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {opciones.map((destino) => (
                  <button
                    key={destino}
                    type="button"
                    disabled={trabajando}
                    onClick={() =>
                      exigeMotivo(orden.estado, destino, false)
                        ? abrirDialogo({ tipo: "ordinario", destino })
                        : aplicar(destino)
                    }
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-neutral-100"
                  >
                    <Check className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                    {etiquetaDeEstado(destino)}
                  </button>
                ))}
              </div>
            )}

            {conSalto && disponibles !== null ? (
              <button
                type="button"
                onClick={() => abrirDialogo({ tipo: "forzar" })}
                className="mt-1 flex w-full items-center gap-2 border-neutral-100 border-t px-2 pt-2 pb-1 text-left text-amber-700 text-xs hover:text-amber-800"
              >
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                Forzar estado…
              </button>
            ) : null}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {dialogo ? (
        <DialogoCambioDeEstado
          numero={orden.numero}
          estadoActual={orden.estado}
          modo={dialogo}
          onCerrar={() => setDialogo(null)}
          alConfirmar={async (destino, motivo) => {
            await cambiarEstadoOrden(orden.id, destino, {
              motivo,
              forzar: dialogo.tipo === "forzar",
            });
            await alCambiar();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Impresora, editable desde la fila.
 *
 * Quedó pendiente de la fase 3 porque **no había endpoint que diera la lista**:
 * la API de impresoras llegó después. El cambio va por `PATCH
 * /produccion/:id/asignacion`, el mismo que el responsable.
 *
 * Sólo se ofrecen las **activas**: asignar trabajo a un equipo dado de baja es
 * exactamente lo que la baja quiso evitar. El estado va al lado del nombre
 * —una impresora en mantenimiento se puede asignar, pero conviene verlo antes—.
 */
export function ImpresoraInline({
  orden,
  impresoras,
  alAsignar,
}: {
  orden: OrdenListada;
  impresoras: readonly { id: string; nombre: string; estado: EstadoImpresora }[];
  alAsignar: (impresoraId: string | null) => Promise<void>;
}) {
  const { can } = useSession();
  const [trabajando, setTrabajando] = useState(false);

  if (esSoloLectura(orden.estado) || !can("produccion", "asignar")) {
    return <span className="text-neutral-600">{orden.impresora ?? "sin asignar"}</span>;
  }

  return (
    <select
      aria-label={`Impresora de ${orden.numero}`}
      value={orden.impresoraId ?? ""}
      disabled={trabajando}
      onChange={async (e) => {
        setTrabajando(true);
        try {
          await alAsignar(e.target.value || null);
        } finally {
          setTrabajando(false);
        }
      }}
      className="rounded border border-transparent bg-transparent px-1 py-0.5 text-neutral-600 text-sm hover:border-neutral-300 hover:bg-white"
    >
      <option value="">sin asignar</option>
      {impresoras.map((i) => (
        <option key={i.id} value={i.id}>
          {i.nombre} · {ESTADOS_IMPRESORA[i.estado].etiqueta}
        </option>
      ))}
    </select>
  );
}

/**
 * Responsable, editable desde la fila.
 *
 * Un `select` y no un popover: la lista es corta y no hay nada que confirmar.
 * `imprenta` usa popover con buscador, que tiene sentido con cientos de
 * empleados; acá la organización es una sola imprenta.
 */
export function ResponsableInline({
  orden,
  usuarios,
  alAsignar,
}: {
  orden: OrdenListada;
  usuarios: readonly { id: string; nombre: string }[];
  alAsignar: (responsableId: string | null) => Promise<void>;
}) {
  const { can } = useSession();
  const [trabajando, setTrabajando] = useState(false);

  if (esSoloLectura(orden.estado) || !can("produccion", "asignar")) {
    return <span className="text-neutral-600">{orden.responsable ?? "sin asignar"}</span>;
  }

  return (
    <select
      aria-label={`Responsable de ${orden.numero}`}
      value={orden.responsableId ?? ""}
      disabled={trabajando}
      onChange={async (e) => {
        setTrabajando(true);
        try {
          await alAsignar(e.target.value || null);
        } finally {
          setTrabajando(false);
        }
      }}
      className="rounded border border-transparent bg-transparent px-1 py-0.5 text-neutral-600 text-sm hover:border-neutral-300 hover:bg-white"
    >
      <option value="">sin asignar</option>
      {usuarios.map((u) => (
        <option key={u.id} value={u.id}>
          {u.nombre}
        </option>
      ))}
    </select>
  );
}
